import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import { api_Routes } from "../../api_Routes";
import { Helper } from "../../tools/Helper";

interface CreateChannelDialogProps {
  open: boolean;
  onClose: () => void;
  onGroupCreated: () => void;
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export const CreateChannelDialog: React.FC<CreateChannelDialogProps> = ({
  open,
  onClose,
  onGroupCreated,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    avatar: null as File | null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        avatar: file,
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      if (formData.description) {
        formDataToSend.append("description", formData.description);
      }
      if (formData.avatar) {
        formDataToSend.append("avatar", formData.avatar);
      }

      // Send members as an array of IDs

      const { response, message } = await Helper.Post({
        url: api_Routes.UserChat.CreateChannel,
        hasToken: true,
        data: formDataToSend as any,
      });

      if (response) {
        onGroupCreated();
        onClose();
      } else {
        setError(message || "Failed to create group");
      }
    } catch (err) {
      setError("An error occurred while creating the group");
      console.error("Error creating group:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Channel</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <IconButton
                component="label"
                sx={{
                  width: 100,
                  height: 100,
                  border: "2px dashed",
                  borderColor: "divider",
                }}
              >
                {avatarPreview ? (
                  <Avatar
                    src={avatarPreview}
                    sx={{ width: "100%", height: "100%" }}
                  />
                ) : (
                  <AddPhotoAlternateIcon sx={{ fontSize: 40 }} />
                )}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                />
              </IconButton>
            </Box>

            <TextField
              name="name"
              label="Channel Name"
              value={formData.name}
              onChange={handleInputChange}
              required
              fullWidth
            />

            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
            />

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.name}
          >
            {loading ? "Creating..." : "Create Channel"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
